import { useContext } from "react";

import { UserContext } from "@/app/providers";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import {
	MaterialHandlingSearchResponse,
	MaterialHandlingSearchResponseItem,
} from "@/features/material-handling/model/material-handling/MaterialHandlingSearchResponse";
import { StorageKeys } from "@/storage/storageKeys";
import { useQuery } from "@tanstack/react-query";

import { GroupList } from "@/model/Application/GroupList";

import { ReadFromLocalStorage, WriteToLocalStorage } from "@/service/Utilities";

// TODO: Ustin - this whole file is a mess. A mix of queries and local storage operations. Need to refactor it.
export const useFetchMaterialHandlingProcedure = (
	materialHandlingId: number | undefined,
	materialHandlingTagId: string,
) => {
	return useQuery({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			materialHandlingId,
			materialHandlingTagId,
		],
		queryFn: () =>
			SATOSPortal.GetMaterialHandlingProcedure(
				materialHandlingId,
				materialHandlingTagId,
			),
		enabled: !!materialHandlingId,
	});
};

export const useFetchMaterialHandlingProcedureForEdit = (
	materialHandlingId: number | undefined,
	materialHandlingTagId: string,
	doLoad: boolean,
) => {
	return useQuery({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			materialHandlingId,
			materialHandlingTagId,
			MaterialHandlingQueryKeys.edit,
		],
		queryFn: () =>
			SATOSPortal.GetMaterialHandlingProcedure(
				materialHandlingId,
				materialHandlingTagId,
				true,
			),
		enabled: !!materialHandlingId && doLoad,
	});
};

export const useFetchUpdatedMaterialHandlingProcedures = (
	companyId: number | undefined,
) => {
	return useQuery({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingQueryKeys.list,
			companyId,
		],
		queryFn: () => SATOSPortal.GetMaterialHandlingProcedures(),
		enabled: !!companyId,
	});
};

export const useFetchMaterialHandlingProceduresWaitingForApproval = (
	companyId: number | undefined,
) => {
	return useQuery({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingQueryKeys.list,
			MaterialHandlingQueryKeys.approve,
			companyId,
		],
		queryFn: () =>
			SATOSPortal.GetMaterialHandlingProceduresWaitingForApproval(),
		enabled: !!companyId,
	});
};

// TODO: This is used in procedure module. We shouldn't mix queries from different modules.
export const useFetchUserGroups = () => {
	const user = useContext(UserContext);
	return useQuery<GroupList>({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingQueryKeys.groups,
			"GetUserGroups",
			user?.CompanyId,
		],
		queryFn: () => SATOSPortal.GetMaterialHandlingGroups(),
	});
};

export const useFetchMaterialHandlingProceduresByTag = (searchTerm: string) => {
	return useQuery<MaterialHandlingSearchResponse>({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingQueryKeys.search,
			searchTerm,
		],
		queryFn: () => SATOSPortal.SearchMaterialHandlingTag(searchTerm),
		enabled: !!searchTerm && !isEmpty(searchTerm),
	});
};

export const useFetchMaterialHandlingArchiveProcedure = (id: number) => {
	return useQuery({
		queryKey: [MaterialHandlingQueryKeys.materialHandling, id],
		queryFn: () => SATOSPortal.GetMaterialHandlingArchiveProcedure(id),
		enabled: !!id,
	});
};

export class MaterialHandlingQueryKeys {
	static readonly materialHandling = "materialHandling";
	static readonly approve = "approve";
	static readonly edit = "edit";
	static readonly list = "list";
	static readonly search = "search";
	static readonly groups = "groups";
	static readonly updated = "updated";
	static readonly import = "import";
}

export function isEmpty(text: string): boolean {
	return text == null || text.match(/^\s*$/) !== null;
}

export const useMaterialHandlingLocalFavorites = () => {
	return {
		favorites: ReadFromLocalStorage<Array<MaterialHandlingSearchResponseItem>>(
			StorageKeys.materialHandlingFavorites,
			[],
		),
		maxNumberOfFavorites: ReadFromLocalStorage<number>(
			StorageKeys.materialHandlingMaxFavorites,
			10,
		),
	};
};

export const useWriteMaterialHandlingLocalFavoritesToLocalStorage = (
	favorite: MaterialHandlingSearchResponseItem,
) => {
	const { favorites, maxNumberOfFavorites } =
		useMaterialHandlingLocalFavorites();

	const idx = favorites.findIndex(
		(fav) => fav.MaterialHandlingId === favorite?.MaterialHandlingId,
	);

	if (idx === -1) {
		WriteToLocalStorage<Array<MaterialHandlingSearchResponseItem>>(
			StorageKeys.materialHandlingFavorites,
			[favorite, ...getTrimmedFavorites(favorites, maxNumberOfFavorites)],
		);
	}
};

const getTrimmedFavorites = (
	faves: Array<MaterialHandlingSearchResponseItem>,
	maxFavorites: number,
) => {
	if (faves.length >= maxFavorites) {
		return faves.slice(0, -1);
	}
	return faves;
};
